import { api } from "../api";
import {
	FALLBACK_ONBOARDING_CONFIG,
	type OnboardingConfig,
	type StatusFlowRule,
} from "./flowConfig.fallback";

// Wire shape of GET /api/onboarding/config (era-api contract): the per-status
// rules live under `flowRules`, and each rule may omit `exclude` / carry extra
// server-only keys (s9, roleGender, skipToS10). We normalize to the internal
// OnboardingConfig shape (`flow`, exclude always present) at this boundary.
interface ApiOnboardingConfig {
	enums: OnboardingConfig["enums"];
	flowRules: Record<
		keyof OnboardingConfig["flow"],
		{
			roles: StatusFlowRule["roles"];
			s7: { show: boolean; exclude?: StatusFlowRule["s7"]["exclude"] };
			s8: { show: boolean };
		}
	>;
}

// Fetches the server-authoritative flow rules + enums. Until
// GET /api/onboarding/config is deployed, any failure falls back to the
// hardcoded contract §3/§4 config so the flow runs unchanged (spec §3).
export async function getOnboardingConfig(): Promise<OnboardingConfig> {
	try {
		const response = await api.get<ApiOnboardingConfig>(
			"/api/onboarding/config",
		);
		const { enums, flowRules } = response.data;
		if (!enums || !flowRules) {
			return FALLBACK_ONBOARDING_CONFIG;
		}
		const flow = Object.fromEntries(
			Object.entries(flowRules).map(([status, rule]) => [
				status,
				{
					roles: rule.roles,
					s7: { show: rule.s7.show, exclude: rule.s7.exclude ?? [] },
					s8: { show: rule.s8.show },
				},
			]),
		) as OnboardingConfig["flow"];
		return { enums, flow };
	} catch {
		return FALLBACK_ONBOARDING_CONFIG;
	}
}
